import React, { useCallback, useEffect, useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import SouthWestRoundedIcon from "@mui/icons-material/SouthWestRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import {
  deleteEstimateComm,
  deleteEstimateRfq,
  getEstimateCommBody,
  getEstimateCommunications,
  isTommyClient,
  markPresupuestoOffered,
} from "../../api/Estimates/estimateRfqApi";
import EstimatePedirCosteDialog from "./EstimatePedirCosteDialog";
import EstimateEnviarOfertaClienteDialog from "./EstimateEnviarOfertaClienteDialog";
import EstimateTommyOfferedDialog from "./EstimateTommyOfferedDialog";
import EstimateReplyDialog from "./EstimateReplyDialog";
import EstimateCommAttachmentThumbs from "./EstimateCommAttachmentThumbs";
import EstimateInsertarCotizacionLineasDialog from "./EstimateInsertarCotizacionLineasDialog";
import { socket } from "../../socket";

function rfqEstadoLabel(estado) {
  switch (String(estado || "")) {
    case "Draft":
      return "Borrador";
    case "Sent":
      return "Enviada";
    case "Quoted":
      return "Cotizada";
    case "Applied":
      return "Aplicada";
    case "Cancelled":
      return "Cancelada";
    case "Expired":
      return "Caducada";
    default:
      return estado || "—";
  }
}

function rfqTone(estado) {
  switch (String(estado || "")) {
    case "Sent":
      return "info";
    case "Quoted":
      return "warning";
    case "Applied":
      return "success";
    case "Cancelled":
    case "Expired":
      return "default";
    default:
      return "default";
  }
}

function formatWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

/** Limpia snippets con basura de firma / cid para el timeline */
function cleanEmailSnippet(raw) {
  let s = String(raw || "");
  if (!s.trim()) return "";
  s = s.replace(/\[cid:[^\]]+\]/gi, " ");
  s = s.replace(/cid:[0-9a-f-]{8,}/gi, " ");
  s = s.replace(/_{5,}/g, " ");
  s = s.replace(/\s+/g, " ").trim();
  // Corta el resto de firma típica
  s = s.split(/\bWith best regards\b/i)[0].trim();
  s = s.split(/\bDe:\s*\S+@/i)[0].trim();
  return s.slice(0, 280);
}

/** Filtra solo firmas/inline evidentes; no ocultar adjuntos reales */
function filterRealAttachments(list, bodyHtml = "", bodyText = "") {
  const blob = `${bodyHtml || ""}\n${bodyText || ""}`;
  const hasCidRefs = /cid:/i.test(blob);
  const cidRefs = new Set();
  for (const m of blob.matchAll(/cid:([^"'\s>\]]+)/gi)) {
    cidRefs.add(
      String(m[1] || "")
        .replace(/^<|>$/g, "")
        .toLowerCase(),
    );
  }

  return (Array.isArray(list) ? list : []).filter((a) => {
    if (!a?.id) return false;
    const filename = String(a.filename || "");
    const cid = a.cid ? String(a.cid).replace(/^<|>$/g, "") : "";
    const cidLower = cid.toLowerCase();

    // Partes inline generadas al enviar (firma / cid embebido)
    if (/^(inline|signature)[-_]?\d+\./i.test(filename)) {
      return false;
    }
    if (/^signature[-_]/i.test(filename)) {
      return false;
    }
    // Firma típica: image.png / logo + cid en el cuerpo
    if (
      hasCidRefs &&
      /^(image|logo|signature-logo)\.(png|jpe?g|gif|webp)$/i.test(filename)
    ) {
      return false;
    }
    // Solo descartar cid si realmente se referencia en el HTML (inline)
    if (cid && cidRefs.size && cidRefs.has(cidLower)) {
      return false;
    }
    // cid genérico de firma sin estar en el cuerpo
    if (cid && /^(signature|logo|cid[-_]?img)/i.test(cid)) {
      return false;
    }
    return true;
  });
}

/** Cuenta de adjuntos guardada en Snippet del CommLink RFQ (p. ej. "2 adjuntos") */
function attachmentCountFromSnippet(snippet) {
  const m = String(snippet || "").match(/(\d+)\s*adjuntos?/i);
  return m ? Number(m[1]) : 0;
}

function attachmentCountFromComm(comm) {
  try {
    const raw = comm?.AttachmentsJson;
    if (raw) {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (Array.isArray(parsed) && parsed.length) return parsed.length;
    }
  } catch {
    /* ignore */
  }
  return attachmentCountFromSnippet(comm?.Snippet);
}

function splitEmails(raw) {
  return String(raw || "")
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function rfqAttachmentHint(rfq, communications) {
  const linked = (communications || []).find(
    (c) =>
      Number(c.IdEstimateRfq) === Number(rfq?.IdEstimateRfq) &&
      String(c.Tipo || "").toLowerCase() === "rfq",
  );
  return attachmentCountFromSnippet(linked?.Snippet);
}

/** Colores de chip por tipo de comunicación — legibles de un vistazo */
function tipoChipStyle(tipo, theme) {
  const key = String(tipo || "")
    .toLowerCase()
    .trim();
  const map = {
    rfq: {
      label: "RFQ",
      // Teal CRM — envío formal
      color: theme.palette.primary.dark || theme.palette.primary.main,
      bg: alpha(theme.palette.primary.main, 0.12),
    },
    reply: {
      label: "Reply",
      // Azul vivo — contraste claro con el teal del RFQ
      color: "#0d47a1",
      bg: alpha("#2196f3", 0.16),
    },
    manual: {
      label: "Manual",
      color: theme.palette.warning.dark,
      bg: alpha(theme.palette.warning.main, 0.18),
    },
    link: {
      label: "Enlazado",
      color: "#6a1b9a",
      bg: alpha("#9c27b0", 0.12),
    },
  };
  const hit = map[key];
  if (hit) return hit;
  return {
    label: tipo || "—",
    color: theme.palette.text.secondary,
    bg: alpha(theme.palette.text.primary, 0.06),
  };
}

function SectionHeader({ title, count, theme, accent }) {
  const color = accent || theme.palette.primary.main;
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
      <Typography
        variant="overline"
        sx={{
          fontWeight: 800,
          letterSpacing: "0.08em",
          color: accent ? color : "text.secondary",
          lineHeight: 1,
        }}
      >
        {title}
      </Typography>
      {count != null ? (
        <Chip
          size="small"
          label={count}
          sx={{
            height: 20,
            minWidth: 28,
            fontWeight: 700,
            fontSize: "0.7rem",
            bgcolor: alpha(color, 0.12),
            color,
            border: "none",
          }}
        />
      ) : null}
      <Box
        sx={{
          flex: 1,
          height: 1,
          background: `linear-gradient(90deg, ${alpha(color, 0.35)} 0%, ${alpha(
            theme.palette.divider,
            0.35,
          )} 100%)`,
        }}
      />
    </Stack>
  );
}

function EmptyState({ icon, title, hint, action, theme, accent }) {
  const color = accent || theme.palette.primary.main;
  return (
    <Box
      sx={{
        py: 3.5,
        px: 2,
        textAlign: "center",
        borderRadius: 3,
        border: "1px dashed",
        borderColor: alpha(color, 0.28),
        background: `radial-gradient(120% 80% at 50% 0%, ${alpha(
          color,
          0.12,
        )} 0%, ${alpha(theme.palette.common.white, 0.9)} 55%)`,
      }}
    >
      <Avatar
        sx={{
          width: 52,
          height: 52,
          mx: "auto",
          mb: 1.35,
          bgcolor: alpha(color, 0.14),
          color,
          boxShadow: `0 8px 20px ${alpha(color, 0.18)}`,
        }}
      >
        {icon}
      </Avatar>
      <Typography variant="body2" fontWeight={800} sx={{ mb: 0.35 }}>
        {title}
      </Typography>
      {hint ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            maxWidth: 380,
            mx: "auto",
            mb: action ? 1.5 : 0,
            lineHeight: 1.5,
          }}
        >
          {hint}
        </Typography>
      ) : null}
      {action || null}
    </Box>
  );
}

const EstimateComunicacionesPanel = ({
  theme,
  presupuesto,
  proveedores = [],
  lines = [],
  onCostApplied,
  /** "supplier" (default) | "client" */
  party = "supplier",
  onClientOfferSent,
  precioUnitario,
  totalVenta,
  moneda,
}) => {
  const isClientParty = String(party || "supplier").toLowerCase() === "client";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rfqs, setRfqs] = useState([]);
  const [comms, setComms] = useState([]);
  const [pedirOpen, setPedirOpen] = useState(false);
  const [clientOfferOpen, setClientOfferOpen] = useState(false);
  const [tommyOfferedOpen, setTommyOfferedOpen] = useState(false);
  const [tommyOfferedConfirming, setTommyOfferedConfirming] = useState(false);
  const [emailView, setEmailView] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // deleteTarget: { kind: 'comm'|'rfq', item, mode: 'unlink'|'trash' }
  const [deleting, setDeleting] = useState(false);
  const [replySource, setReplySource] = useState(null);
  // replySource: { kind: 'comm'|'rfq', item }
  const [quoteRfq, setQuoteRfq] = useState(null);

  const presupuestoId = presupuesto?.IdPresupuesto;
  const tommyClient = isTommyClient({
    idCliente: presupuesto?.IdCliente,
    clienteNombre: presupuesto?.ClienteNombre,
  });
  const isCreated = String(presupuesto?.Estado || "") === "Created";

  const openClientOfferFlow = () => {
    if (tommyClient) {
      setTommyOfferedOpen(true);
      return;
    }
    setClientOfferOpen(true);
  };

  // El envío RFQ original ya se ve en "Peticiones de coste"; el timeline
  // muestra replies y emails enlazados (evita duplicar el mismo mensaje).
  // En pestaña cliente solo se muestran Party=client.
  const partyComms = useMemo(() => {
    const want = isClientParty ? "client" : "supplier";
    return (comms || []).filter((c) => {
      const p = String(c.Party || "supplier").toLowerCase();
      if (isClientParty) return p === "client";
      // Proveedores: supplier + legacy sin party
      return p === "supplier" || p === want || !c.Party;
    });
  }, [comms, isClientParty]);

  const timelineComms = useMemo(() => {
    if (isClientParty) return partyComms;
    const rfqIds = new Set(
      (rfqs || []).map((r) => Number(r.IdEstimateRfq)).filter(Number.isFinite),
    );
    return (partyComms || []).filter((c) => {
      const tipo = String(c.Tipo || "").toLowerCase();
      if (tipo !== "rfq") return true;
      const id = Number(c.IdEstimateRfq);
      return !(Number.isFinite(id) && rfqIds.has(id));
    });
  }, [partyComms, rfqs, isClientParty]);

  const timelineInboundCount = useMemo(
    () =>
      timelineComms.filter(
        (c) => String(c.Direction || "").toLowerCase() === "in",
      ).length,
    [timelineComms],
  );

  const openEmailView = async (item) => {
    const direction = String(item?.Direction || "").toLowerCase();
    const isInbound = direction === "in";
    const tipo = String(item?.Tipo || "").toLowerCase();
    const isReply =
      tipo === "reply" || /^re\s*:/i.test(String(item?.Subject || ""));

    // Solo el envío RFQ original puede reutilizar BodyHtml guardado.
    // Replies (aunque sean "out", p.ej. Imma→Imma) deben ir a MATRIXBOX.
    let bodyHtml = null;
    if (!isInbound && !isReply) {
      bodyHtml =
        item?.BodyHtml ||
        (item?.IdEstimateRfq
          ? rfqs.find(
              (r) => Number(r.IdEstimateRfq) === Number(item.IdEstimateRfq),
            )?.BodyHtml
          : null) ||
        null;
    }

    const base = {
      subject: item?.Subject || "—",
      fromEmail: item?.FromEmail || "",
      toEmail: item?.ToEmail || "",
      when: formatWhen(item?.SentAt || item?.CreatedAt),
      bodyHtml,
      snippet: item?.Snippet || "",
      attachments: [],
      commId: item?.IdEstimateCommLink || null,
    };

    setEmailView(base);

    let commId = item?.IdEstimateCommLink || null;
    if (!commId && item?.IdEstimateRfq) {
      const linked = (comms || []).find(
        (c) =>
          Number(c.IdEstimateRfq) === Number(item.IdEstimateRfq) &&
          String(c.Tipo || "").toLowerCase() === "rfq",
      );
      commId = linked?.IdEstimateCommLink || null;
      if (commId) {
        setEmailView((prev) => (prev ? { ...prev, commId } : prev));
      }
    }
    if (!commId) return;

    const hasCachedHtml = Boolean(bodyHtml);
    setEmailLoading(!hasCachedHtml);
    try {
      const body = await getEstimateCommBody(commId);
      const html = body?.html || body?.bodyHtml || "";
      const text = body?.text || "";
      const rawAtts = Array.isArray(body?.attachments) ? body.attachments : [];
      const attachments = filterRealAttachments(rawAtts, html, text);
      if (
        process.env.NODE_ENV === "development" &&
        rawAtts.length > 0 &&
        attachments.length === 0
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          "[EstimateComms] La API devolvió adjuntos pero el filtro los ocultó:",
          rawAtts,
        );
      }
      if (process.env.NODE_ENV === "development" && rawAtts.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("[EstimateComms] /body sin adjuntos", {
          commId,
          source: body?.source,
          keys: body && Object.keys(body),
        });
      }
      // Historial del hilo: otros emails relacionados (solo misma party)
      const threadSource = isClientParty ? partyComms : comms;
      const thread = (threadSource || [])
        .filter((c) => {
          if (Number(c.IdEstimateCommLink) === Number(commId)) return false;
          if (isClientParty) {
            const cParty = String(c.Party || "").toLowerCase();
            const cTipo = String(c.Tipo || "").toLowerCase();
            const cDir = String(c.Direction || "").toLowerCase();
            if (cParty !== "client" || cTipo !== "offer" || cDir !== "out") {
              return false;
            }
          }
          if (
            item?.IdEstimateRfq &&
            Number(c.IdEstimateRfq) === Number(item.IdEstimateRfq)
          ) {
            return true;
          }
          const a = String(item?.Subject || "")
            .replace(/^re:\s*/i, "")
            .trim()
            .toLowerCase();
          const b = String(c.Subject || "")
            .replace(/^re:\s*/i, "")
            .trim()
            .toLowerCase();
          return a && b && a === b;
        })
        .slice(0, 8);

      setEmailView((prev) =>
        prev
          ? {
              ...prev,
              commId,
              bodyHtml: html || prev.bodyHtml || null,
              // No pasar HTML por cleanEmailSnippet (solo timeline)
              snippet: text || cleanEmailSnippet(prev.snippet) || "",
              attachments,
              thread,
            }
          : prev,
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[EstimateComms] Error cargando cuerpo/adjuntos:", err);
      setEmailView((prev) =>
        prev
          ? {
              ...prev,
              snippet:
                prev.snippet ||
                item?.Snippet ||
                "No se pudo cargar el cuerpo del email",
            }
          : prev,
      );
    } finally {
      setEmailLoading(false);
    }
  };

  const load = useCallback(
    async (opts = {}) => {
      if (!presupuestoId) return;
      const silent = Boolean(opts.silent);
      if (!silent) {
        setLoading(true);
        setError("");
      }
      try {
        const data = await getEstimateCommunications(presupuestoId, opts);
        setRfqs(data.rfqs || []);
        setComms(data.communications || []);
        if (data.sync?.linked > 0 || !silent) {
          setSyncInfo(data.sync || null);
        } else if (data.sync?.warning) {
          setSyncInfo((prev) => ({ ...(prev || {}), ...data.sync }));
        }
      } catch (err) {
        if (!silent) {
          setError(
            [
              err?.response?.data?.error,
              err?.response?.data?.detail,
              err?.message,
            ]
              .filter(Boolean)
              .join(" — ") || "Error cargando comunicaciones",
          );
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [presupuestoId],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh: socket (enlace desde Matrix / sync) + polling suave + foco
  useEffect(() => {
    if (!presupuestoId) return undefined;

    let cancelled = false;
    let debounceTimer = null;
    const softReload = (withSync = true) => {
      if (cancelled) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        load({
          silent: true,
          sync: withSync,
        }).catch(() => {});
      }, 400);
    };

    const onSocket = (payload) => {
      if (Number(payload?.IdPresupuesto) !== Number(presupuestoId)) return;
      // El cambio ya está en DB; solo refrescar lista (sin sync pesado)
      softReload(false);
    };

    socket.on("estimate-comms-updated", onSocket);

    const pollMs = 25000;
    const pollId = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      softReload(true);
    }, pollMs);

    const onVisible = () => {
      if (document.visibilityState === "visible") softReload(true);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      clearInterval(pollId);
      socket.off("estimate-comms-updated", onSocket);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [presupuestoId, load]);

  const confirmDeleteComm = async () => {
    if (!deleteTarget?.item) return;
    setDeleting(true);
    setError("");
    try {
      const mode = deleteTarget.mode || "unlink";
      let result;
      if (deleteTarget.kind === "rfq") {
        result = await deleteEstimateRfq(deleteTarget.item.IdEstimateRfq, mode);
      } else {
        result = await deleteEstimateComm(
          deleteTarget.item.IdEstimateCommLink,
          mode,
        );
      }
      const warning = result?.warning || null;
      const removedCommId =
        deleteTarget.kind === "comm"
          ? deleteTarget.item?.IdEstimateCommLink
          : null;
      setDeleteTarget(null);
      if (
        removedCommId &&
        Number(emailView?.commId) === Number(removedCommId)
      ) {
        setEmailView(null);
      }
      // Sin sync: evita re-enlazar y no alarga el "Procesando…"
      await load({ sync: false });
      if (warning) {
        setSyncInfo((prev) => ({ ...(prev || {}), warning }));
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "No se pudo eliminar la comunicación",
      );
    } finally {
      setDeleting(false);
    }
  };

  if (!presupuestoId) {
    return (
      <EmptyState
        theme={theme}
        icon={<MailOutlineIcon />}
        title="Guarda el presupuesto"
        hint="Cuando el presupuesto esté guardado podrás pedir costes y ver el timeline de emails."
      />
    );
  }

  const actionBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 2,
    px: 1.5,
  };

  const clientAccent = "#00c2a3";
  const panelAccent = isClientParty ? clientAccent : theme.palette.primary.main;
  const lastClientOffer = timelineComms[0] || null;
  const clientAttachTotal = timelineComms.reduce(
    (acc, c) => acc + attachmentCountFromComm(c),
    0,
  );

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(panelAccent, isClientParty ? 0.18 : 0.1),
        background: isClientParty
          ? `linear-gradient(165deg, ${alpha(clientAccent, 0.1)} 0%, ${alpha(
              "#0d9488",
              0.04,
            )} 36%, ${alpha(theme.palette.common.white, 0.96)} 72%)`
          : `linear-gradient(180deg, ${alpha(
              theme.palette.primary.main,
              0.035,
            )} 0%, ${alpha(theme.palette.common.white, 0.92)} 48px)`,
        px: { xs: 1.25, md: 1.75 },
        pt: 1.5,
        pb: 1.75,
        boxShadow: isClientParty
          ? `0 10px 28px ${alpha(clientAccent, 0.08)}`
          : "none",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.75, flexWrap: "wrap", gap: 1 }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: alpha(panelAccent, 0.14),
              color: panelAccent,
              boxShadow: `0 6px 16px ${alpha(panelAccent, 0.22)}`,
            }}
          >
            <MailOutlineIcon fontSize="small" />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={800}
              sx={{ lineHeight: 1.2 }}
            >
              {isClientParty ? "Email a clientes" : "Comunicaciones / RFQ"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isClientParty
                ? `${timelineComms.length} oferta${timelineComms.length === 1 ? "" : "s"} enviada${timelineComms.length === 1 ? "" : "s"}`
                : `${rfqs.length} petición${rfqs.length === 1 ? "" : "es"} · ${timelineComms.length} email${timelineComms.length === 1 ? "" : "s"}${
                    timelineInboundCount > 0
                      ? ` · ${timelineInboundCount} recibido${timelineInboundCount === 1 ? "" : "s"}`
                      : ""
                  }`}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Actualizar y sincronizar con MATRIXBOX">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={load}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <RefreshRoundedIcon fontSize="small" />
                  )
                }
                sx={{
                  ...actionBtnSx,
                  ...(isClientParty
                    ? {
                        borderColor: alpha(clientAccent, 0.45),
                        color: clientAccent,
                        "&:hover": {
                          borderColor: clientAccent,
                          bgcolor: alpha(clientAccent, 0.06),
                        },
                      }
                    : {}),
                }}
              >
                Actualizar
              </Button>
            </span>
          </Tooltip>
          {isClientParty ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<NorthEastRoundedIcon />}
              onClick={openClientOfferFlow}
              sx={{
                ...actionBtnSx,
                bgcolor: clientAccent,
                "&:hover": { bgcolor: "#00a88c" },
                boxShadow: `0 8px 20px ${alpha(clientAccent, 0.32)}`,
              }}
            >
              {tommyClient
                ? isCreated
                  ? "Marcar Offered (Tommy)"
                  : "Tommy · sin email"
                : isCreated
                  ? "Enviar oferta (Offered)"
                  : "Reenviar oferta"}
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={<MailOutlineIcon />}
              onClick={() => setPedirOpen(true)}
              sx={{
                ...actionBtnSx,
                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.28)}`,
              }}
            >
              Pedir coste
            </Button>
          )}
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      {syncInfo?.linked > 0 ? (
        <Alert severity="success" sx={{ mb: 1.5, borderRadius: 2 }}>
          Se han vinculado {syncInfo.linked} respuesta(s) del proveedor desde
          MATRIXBOX.
        </Alert>
      ) : null}

      {syncInfo?.warning ? (
        <Alert
          severity="warning"
          sx={{ mb: 1.5, borderRadius: 2 }}
          onClose={() => setSyncInfo(null)}
        >
          {syncInfo.warning}
        </Alert>
      ) : null}

      {loading &&
      (isClientParty
        ? timelineComms.length === 0
        : rfqs.length === 0 && comms.length === 0) ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress size={28} />
        </Box>
      ) : isClientParty ? (
        <Stack spacing={2}>
          {timelineComms.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(3, minmax(0, 1fr))",
                },
                gap: 1,
              }}
            >
              {[
                {
                  label: "Ofertas enviadas",
                  value: String(timelineComms.length),
                  hint: "histórico de este presupuesto",
                },
                {
                  label: "Último envío",
                  value: lastClientOffer
                    ? formatWhen(
                        lastClientOffer.SentAt || lastClientOffer.CreatedAt,
                      )
                    : "—",
                  hint: "fecha del email más reciente",
                },
                {
                  label: "Adjuntos",
                  value: String(clientAttachTotal),
                  hint: "PDF y archivos en ofertas",
                },
              ].map((stat) => (
                <Box
                  key={stat.label}
                  sx={{
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: alpha(clientAccent, 0.18),
                    bgcolor: alpha(theme.palette.common.white, 0.82),
                    px: 1.5,
                    py: 1.15,
                    backgroundImage: `linear-gradient(135deg, ${alpha(
                      clientAccent,
                      0.08,
                    )} 0%, transparent 55%)`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: alpha(clientAccent, 0.9),
                      fontSize: "0.65rem",
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    sx={{ lineHeight: 1.25, mt: 0.35, color: "text.primary" }}
                    noWrap
                    title={stat.value}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.15, lineHeight: 1.3 }}
                  >
                    {stat.hint}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}

          <Box>
            <SectionHeader
              title="Ofertas enviadas al cliente"
              count={timelineComms.length}
              theme={theme}
              accent={clientAccent}
            />
            {timelineComms.length === 0 ? (
              <EmptyState
                theme={theme}
                accent={clientAccent}
                icon={<MailOutlineIcon />}
                title="Todavía no hay ofertas enviadas"
                hint={
                  tommyClient
                    ? "Tommy no recibe email de oferta. Al ofertar solo se marca el presupuesto como Offered."
                    : "Cuando pases a Offered o pulses Reenviar oferta, aquí verás el histórico con destinatarios, adjuntos y el cuerpo del email."
                }
                action={
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<NorthEastRoundedIcon />}
                    onClick={openClientOfferFlow}
                    sx={{
                      mt: 0.5,
                      bgcolor: clientAccent,
                      fontWeight: 800,
                      textTransform: "none",
                      borderRadius: 2,
                      boxShadow: `0 8px 18px ${alpha(clientAccent, 0.28)}`,
                      "&:hover": { bgcolor: "#00a88c" },
                    }}
                  >
                    {tommyClient
                      ? "Marcar Offered (sin email)"
                      : "Enviar primera oferta"}
                  </Button>
                }
              />
            ) : (
              <Stack spacing={1.35} sx={{ position: "relative" }}>
                {timelineComms.map((comm, idx) => {
                  const attCount = attachmentCountFromComm(comm);
                  const toList = splitEmails(comm.ToEmail);
                  const snippet = cleanEmailSnippet(comm.Snippet);
                  const isLatest = idx === 0;
                  return (
                    <Box
                      key={comm.IdEstimateCommLink}
                      sx={{
                        position: "relative",
                        display: "flex",
                        gap: 1.25,
                        alignItems: "stretch",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: 28,
                          flexShrink: 0,
                          pt: 1.15,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: alpha(clientAccent, isLatest ? 0.2 : 0.1),
                            color: clientAccent,
                            boxShadow: isLatest
                              ? `0 0 0 4px ${alpha(clientAccent, 0.12)}`
                              : "none",
                          }}
                        >
                          <NorthEastRoundedIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        {idx < timelineComms.length - 1 ? (
                          <Box
                            sx={{
                              flex: 1,
                              width: 2,
                              mt: 0.75,
                              borderRadius: 99,
                              bgcolor: alpha(clientAccent, 0.18),
                              minHeight: 18,
                            }}
                          />
                        ) : null}
                      </Box>

                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          border: "1px solid",
                          borderColor: alpha(
                            clientAccent,
                            isLatest ? 0.28 : 0.14,
                          ),
                          borderLeft: `3px solid ${clientAccent}`,
                          borderRadius: 2.5,
                          p: 1.4,
                          bgcolor: alpha(theme.palette.common.white, 0.96),
                          backgroundImage: isLatest
                            ? `linear-gradient(120deg, ${alpha(
                                clientAccent,
                                0.07,
                              )} 0%, transparent 42%)`
                            : "none",
                          transition:
                            "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
                          "&:hover": {
                            borderColor: alpha(clientAccent, 0.45),
                            boxShadow: `0 8px 22px ${alpha(clientAccent, 0.12)}`,
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          justifyContent="space-between"
                          alignItems={{ sm: "flex-start" }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              sx={{ mb: 0.65, flexWrap: "wrap", gap: 0.5 }}
                            >
                              <Chip
                                size="small"
                                label={isLatest ? "Última oferta" : "Oferta"}
                                sx={{
                                  height: 22,
                                  fontWeight: 800,
                                  fontSize: "0.68rem",
                                  bgcolor: alpha(clientAccent, 0.14),
                                  color: "#0f766e",
                                  border: "none",
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: "text.secondary",
                                }}
                              >
                                {formatWhen(comm.SentAt || comm.CreatedAt)}
                              </Typography>
                              {attCount > 0 ? (
                                <Chip
                                  size="small"
                                  icon={
                                    <AttachFileIcon
                                      sx={{ fontSize: "14px !important" }}
                                    />
                                  }
                                  label={`${attCount} adjunto${attCount === 1 ? "" : "s"}`}
                                  sx={{
                                    height: 22,
                                    fontWeight: 700,
                                    bgcolor: alpha("#64748b", 0.1),
                                    color: "text.secondary",
                                    border: "none",
                                    "& .MuiChip-icon": {
                                      color: "text.secondary",
                                    },
                                  }}
                                />
                              ) : null}
                            </Stack>

                            <Typography
                              variant="body2"
                              fontWeight={800}
                              sx={{
                                mb: 0.55,
                                lineHeight: 1.35,
                                color: "text.primary",
                              }}
                            >
                              {comm.Subject || "Sin asunto"}
                            </Typography>

                            <Stack
                              direction="row"
                              spacing={0.5}
                              useFlexGap
                              flexWrap="wrap"
                              sx={{ mb: snippet ? 0.7 : 0 }}
                            >
                              {toList.slice(0, 3).map((email) => (
                                <Chip
                                  key={email}
                                  size="small"
                                  label={email}
                                  sx={{
                                    height: 22,
                                    maxWidth: 220,
                                    fontSize: "0.68rem",
                                    fontWeight: 600,
                                    bgcolor: alpha(clientAccent, 0.06),
                                    border: "1px solid",
                                    borderColor: alpha(clientAccent, 0.16),
                                  }}
                                />
                              ))}
                              {toList.length > 3 ? (
                                <Chip
                                  size="small"
                                  label={`+${toList.length - 3}`}
                                  sx={{
                                    height: 22,
                                    fontWeight: 700,
                                    bgcolor: alpha(clientAccent, 0.1),
                                  }}
                                />
                              ) : null}
                              {comm.FromEmail ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ alignSelf: "center", pl: 0.25 }}
                                >
                                  De {comm.FromEmail}
                                </Typography>
                              ) : null}
                            </Stack>

                            {snippet ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: 1.45,
                                }}
                              >
                                {snippet}
                              </Typography>
                            ) : null}
                          </Box>

                          <Stack
                            direction="row"
                            spacing={0.25}
                            alignItems="center"
                          >
                            <Tooltip title="Ver email">
                              <IconButton
                                size="small"
                                onClick={() => openEmailView(comm)}
                                sx={{
                                  color: clientAccent,
                                  bgcolor: alpha(clientAccent, 0.08),
                                  border: "1px solid",
                                  borderColor: alpha(clientAccent, 0.18),
                                  "&:hover": {
                                    bgcolor: alpha(clientAccent, 0.16),
                                  },
                                }}
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Quitar del presupuesto">
                              <IconButton
                                size="small"
                                aria-label="Quitar oferta del presupuesto"
                                onClick={() =>
                                  setDeleteTarget({
                                    kind: "comm",
                                    item: comm,
                                    mode: "unlink",
                                    clientOffer: true,
                                  })
                                }
                                sx={{
                                  color: "text.secondary",
                                  "&:hover": {
                                    color: clientAccent,
                                    bgcolor: alpha(clientAccent, 0.1),
                                  },
                                }}
                              >
                                <LinkOffIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          <Box>
            <SectionHeader
              title="Peticiones de coste"
              count={rfqs.length}
              theme={theme}
            />
            {rfqs.length === 0 ? (
              partyComms.length > 0 ? (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.06),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.info.main, 0.18),
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ mb: 0.25 }}
                  >
                    Hay emails enlazados, pero ninguna petición formal
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.45 }}
                  >
                    Los emails del timeline se han vinculado desde MATRIXBOX.
                    Una “petición de coste” es distinta: se crea con{" "}
                    <strong>Pedir coste</strong>. El coste del escandallo se
                    introduce a mano en las líneas.
                  </Typography>
                </Box>
              ) : (
                <EmptyState
                  theme={theme}
                  icon={<RequestQuoteOutlinedIcon />}
                  title="Sin peticiones todavía"
                  hint="Usa Pedir coste (arriba) para enviar un RFQ al proveedor. Las respuestas se vincularán aquí y en el timeline."
                />
              )
            ) : (
              <Stack spacing={1.25}>
                {rfqs.map((rfq) => {
                  const attHint = rfqAttachmentHint(rfq, comms);
                  return (
                    <Box
                      key={rfq.IdEstimateRfq}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha(theme.palette.divider, 0.9),
                        borderRadius: 2.5,
                        p: 1.5,
                        bgcolor: alpha(theme.palette.common.white, 0.92),
                        transition:
                          "border-color 0.15s ease, box-shadow 0.15s ease",
                        "&:hover": {
                          borderColor: alpha(theme.palette.primary.main, 0.35),
                          boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.04)}`,
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1.25}
                        sx={{ flexWrap: "wrap", gap: 1 }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            sx={{ mb: 0.65, flexWrap: "wrap", gap: 0.5 }}
                          >
                            <Chip
                              size="small"
                              label={rfqEstadoLabel(rfq.Estado)}
                              color={rfqTone(rfq.Estado)}
                              sx={{ height: 22, fontWeight: 700 }}
                            />
                            <Typography variant="body2" fontWeight={800} noWrap>
                              {rfq.ProveedorNombre || rfq.ToEmail}
                            </Typography>
                            {rfq.QuotedCost != null ? (
                              <Chip
                                size="small"
                                label={`${rfq.QuotedCost} ${rfq.QuotedCurrency || ""}`.trim()}
                                color="success"
                                variant="outlined"
                                sx={{ height: 22, fontWeight: 700 }}
                              />
                            ) : null}
                            {rfq.QuotedLeadTime ? (
                              <Chip
                                size="small"
                                label={rfq.QuotedLeadTime}
                                variant="outlined"
                                sx={{ height: 22 }}
                              />
                            ) : null}
                            {rfq.QuoteLinesJson ? (
                              <Chip
                                size="small"
                                label="Portal"
                                color="secondary"
                                variant="outlined"
                                sx={{ height: 22, fontWeight: 700 }}
                              />
                            ) : null}
                          </Stack>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ mb: 0.35, lineHeight: 1.35 }}
                          >
                            {rfq.Subject}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatWhen(rfq.SentAt || rfq.CreatedAt)}
                            {rfq.ToEmail ? ` · ${rfq.ToEmail}` : ""}
                          </Typography>
                        </Box>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ flexWrap: "wrap", gap: 0.5 }}
                        >
                          <Button
                            size="small"
                            variant="text"
                            startIcon={
                              <VisibilityOutlinedIcon fontSize="small" />
                            }
                            onClick={() => openEmailView(rfq)}
                            sx={actionBtnSx}
                          >
                            Ver
                          </Button>
                          {attHint > 0 ? (
                            <Tooltip title={`${attHint} adjunto(s)`}>
                              <Chip
                                size="small"
                                icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                                label={attHint}
                                variant="outlined"
                                sx={{
                                  height: 26,
                                  fontWeight: 700,
                                  borderColor: alpha(
                                    theme.palette.primary.main,
                                    0.35,
                                  ),
                                }}
                              />
                            </Tooltip>
                          ) : null}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ReplyRoundedIcon fontSize="small" />}
                            disabled={!rfq.GmailMessageId && !rfq.MessageId}
                            onClick={() =>
                              setReplySource({ kind: "rfq", item: rfq })
                            }
                            sx={actionBtnSx}
                          >
                            Responder
                          </Button>
                          {String(rfq.Estado) !== "Cancelled" &&
                          String(rfq.Estado) !== "Expired" ? (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={
                                <PlaylistAddCheckOutlinedIcon fontSize="small" />
                              }
                              onClick={() => setQuoteRfq(rfq)}
                              sx={actionBtnSx}
                            >
                              {String(rfq.Estado) === "Applied"
                                ? "Reinsertar cotización"
                                : rfq.QuoteLinesJson
                                  ? "Revisar e insertar"
                                  : "Registrar cotización"}
                            </Button>
                          ) : null}
                          <Tooltip title="Quitar petición del presupuesto">
                            <IconButton
                              size="small"
                              aria-label="Quitar petición"
                              onClick={() =>
                                setDeleteTarget({
                                  kind: "rfq",
                                  item: rfq,
                                  mode: "unlink",
                                })
                              }
                              sx={{
                                color: "text.secondary",
                                "&:hover": {
                                  color: "primary.main",
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.08,
                                  ),
                                },
                              }}
                            >
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar petición y email en MATRIXBOX">
                            <IconButton
                              size="small"
                              color="error"
                              aria-label="Eliminar petición"
                              onClick={() =>
                                setDeleteTarget({
                                  kind: "rfq",
                                  item: rfq,
                                  mode: "trash",
                                })
                              }
                              sx={{
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.error.main,
                                    0.08,
                                  ),
                                },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          <Box>
            <SectionHeader
              title="Timeline email"
              count={timelineComms.length}
              theme={theme}
            />
            {timelineComms.length === 0 ? (
              <EmptyState
                theme={theme}
                icon={<MailOutlineIcon />}
                title="Sin respuestas ni emails enlazados"
                hint="El envío RFQ está arriba en Peticiones. Aquí aparecen las replies y los emails que enlaces desde MATRIXBOX."
              />
            ) : (
              <Stack spacing={1.15} sx={{ position: "relative" }}>
                {timelineComms.map((c) => {
                  const isOut =
                    String(c.Direction || "").toLowerCase() === "out";
                  const accent = isOut
                    ? theme.palette.primary.main
                    : theme.palette.success.main;
                  const tipoChip = tipoChipStyle(c.Tipo, theme);
                  return (
                    <Box
                      key={c.IdEstimateCommLink}
                      sx={{
                        position: "relative",
                        display: "flex",
                        gap: 1.25,
                        alignItems: "stretch",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: 28,
                          flexShrink: 0,
                          pt: 1.1,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: alpha(accent, 0.12),
                            color: accent,
                          }}
                        >
                          {isOut ? (
                            <NorthEastRoundedIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <SouthWestRoundedIcon sx={{ fontSize: 16 }} />
                          )}
                        </Avatar>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          border: "1px solid",
                          borderColor: alpha(theme.palette.divider, 0.9),
                          borderLeft: `3px solid ${accent}`,
                          borderRadius: 2.5,
                          p: 1.35,
                          bgcolor: alpha(theme.palette.common.white, 0.95),
                          transition:
                            "border-color 0.15s ease, box-shadow 0.15s ease",
                          "&:hover": {
                            borderColor: alpha(accent, 0.45),
                            boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.04)}`,
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={1}
                          sx={{ gap: 1, flexWrap: "wrap" }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              sx={{ mb: 0.55, flexWrap: "wrap", gap: 0.5 }}
                            >
                              <Typography variant="body2" fontWeight={800}>
                                {isOut ? "Enviado" : "Recibido"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                {isOut ? "nosotros" : "proveedor"}
                              </Typography>
                              {c.Tipo ? (
                                <Chip
                                  size="small"
                                  label={tipoChip.label}
                                  sx={{
                                    height: 20,
                                    fontSize: "0.68rem",
                                    fontWeight: 800,
                                    letterSpacing: "0.02em",
                                    border: "none",
                                    bgcolor: tipoChip.bg,
                                    color: tipoChip.color,
                                  }}
                                />
                              ) : null}
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mb: 0.55, lineHeight: 1.4 }}
                            >
                              {formatWhen(c.SentAt || c.CreatedAt)}
                              {c.FromEmail ? ` · De ${c.FromEmail}` : ""}
                              {c.ToEmail ? ` · Para ${c.ToEmail}` : ""}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{
                                mb: c.Snippet ? 0.45 : 0,
                                lineHeight: 1.35,
                              }}
                            >
                              {c.Subject}
                            </Typography>
                            {c.Snippet ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: 1.45,
                                }}
                              >
                                {cleanEmailSnippet(c.Snippet)}
                              </Typography>
                            ) : null}
                          </Box>
                          <Stack
                            direction="row"
                            spacing={0.25}
                            alignItems="center"
                          >
                            <Tooltip title="Ver email">
                              <IconButton
                                size="small"
                                aria-label="Ver email"
                                onClick={() => openEmailView(c)}
                                sx={{
                                  color: "primary.main",
                                  "&:hover": {
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.08,
                                    ),
                                  },
                                }}
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Responder">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Responder"
                                  disabled={!c.GmailMessageId && !c.MessageId}
                                  onClick={() =>
                                    setReplySource({ kind: "comm", item: c })
                                  }
                                  sx={{
                                    color: "primary.main",
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.08,
                                      ),
                                    },
                                  }}
                                >
                                  <ReplyRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Quitar de este presupuesto">
                              <IconButton
                                size="small"
                                aria-label="Quitar de este presupuesto"
                                onClick={() =>
                                  setDeleteTarget({
                                    kind: "comm",
                                    item: c,
                                    mode: "unlink",
                                  })
                                }
                                sx={{
                                  color: "text.secondary",
                                  "&:hover": {
                                    color: "primary.main",
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.08,
                                    ),
                                  },
                                }}
                              >
                                <LinkOffIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar en MATRIXBOX">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label="Eliminar en MATRIXBOX"
                                  disabled={!c.GmailMessageId && !c.MessageId}
                                  onClick={() =>
                                    setDeleteTarget({
                                      kind: "comm",
                                      item: c,
                                      mode: "trash",
                                    })
                                  }
                                  sx={{
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.error.main,
                                        0.08,
                                      ),
                                    },
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Stack>
      )}

      <EstimatePedirCosteDialog
        open={pedirOpen}
        onClose={() => setPedirOpen(false)}
        presupuesto={presupuesto}
        proveedores={proveedores}
        lines={lines}
        defaultCodProv={presupuesto?.IdProveedor}
        onSent={() => load()}
      />

      <EstimateEnviarOfertaClienteDialog
        open={clientOfferOpen}
        onClose={() => setClientOfferOpen(false)}
        presupuesto={presupuesto}
        lines={lines}
        markOffered={String(presupuesto?.Estado || "") === "Created"}
        precioUnitario={precioUnitario}
        totalVenta={totalVenta}
        moneda={moneda}
        onSent={(result) => {
          load();
          if (typeof onClientOfferSent === "function") {
            onClientOfferSent(result);
          }
        }}
      />

      <EstimateTommyOfferedDialog
        open={tommyOfferedOpen}
        onClose={() => {
          if (!tommyOfferedConfirming) setTommyOfferedOpen(false);
        }}
        confirming={tommyOfferedConfirming}
        alreadyOffered={!isCreated}
        onConfirm={async () => {
          if (!presupuestoId) return;
          setTommyOfferedConfirming(true);
          try {
            const result = await markPresupuestoOffered(presupuestoId);
            setTommyOfferedOpen(false);
            if (typeof onClientOfferSent === "function") {
              onClientOfferSent({
                ...result,
                tommyNoEmail: true,
                presupuesto: {
                  ...(result?.presupuesto || {}),
                  Estado: "Offered",
                  FechaPresentacion:
                    result?.presupuesto?.FechaPresentacion ||
                    new Date().toISOString(),
                },
              });
            }
          } catch (err) {
            setError(
              err?.response?.data?.error ||
                err?.message ||
                "No se pudo marcar como Offered",
            );
          } finally {
            setTommyOfferedConfirming(false);
          }
        }}
      />

      <EstimateReplyDialog
        open={Boolean(replySource)}
        onClose={() => setReplySource(null)}
        presupuesto={presupuesto}
        source={replySource}
        onSent={() => load()}
      />

      <EstimateInsertarCotizacionLineasDialog
        open={Boolean(quoteRfq)}
        onClose={() => setQuoteRfq(null)}
        rfq={quoteRfq}
        onApplied={() => {
          load();
          onCostApplied?.();
        }}
      />

      <Dialog
        open={Boolean(emailView)}
        onClose={() => setEmailView(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pr: 2, pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} component="span">
            {emailView?.subject || "Email"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {emailView ? (
            <Stack spacing={1.5}>
              <Box
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {emailView.when}
                </Typography>
                {emailView.fromEmail ? (
                  <Typography variant="caption" color="text.secondary">
                    {emailView.fromEmail} → {emailView.toEmail || "—"}
                  </Typography>
                ) : null}
              </Box>

              {!emailLoading &&
              Array.isArray(emailView.attachments) &&
              emailView.attachments.length > 0 ? (
                <EstimateCommAttachmentThumbs
                  attachments={emailView.attachments}
                  commId={emailView.commId}
                  theme={theme}
                  onError={setError}
                />
              ) : emailLoading && emailView.bodyHtml ? (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ py: 0.5 }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Cargando adjuntos…
                  </Typography>
                </Stack>
              ) : null}

              {emailLoading && !emailView.bodyHtml ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : emailView.bodyHtml ? (
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                    bgcolor: alpha(theme.palette.common.white, 0.95),
                    "& table": { borderCollapse: "collapse" },
                    "& td, & th": { border: "1px solid #ddd", p: "6px 10px" },
                  }}
                  dangerouslySetInnerHTML={{ __html: emailView.bodyHtml }}
                />
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {emailView.snippet || "Sin contenido disponible."}
                </Typography>
              )}

              {!emailLoading &&
              Array.isArray(emailView.thread) &&
              emailView.thread.length > 0 ? (
                <Box sx={{ pt: 1 }}>
                  <Typography
                    variant="overline"
                    sx={{ fontWeight: 800, letterSpacing: "0.06em" }}
                  >
                    Historial del hilo
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 0.75 }}>
                    {emailView.thread.map((t) => (
                      <Box
                        key={t.IdEstimateCommLink}
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: alpha(theme.palette.common.black, 0.02),
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {formatWhen(t.SentAt || t.CreatedAt)}
                          {" · "}
                          {String(t.Direction || "").toLowerCase() === "out"
                            ? "Enviado"
                            : "Recibido"}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ lineHeight: 1.35 }}
                        >
                          {t.Subject || "—"}
                        </Typography>
                        {t.Snippet ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {cleanEmailSnippet(t.Snippet)}
                          </Typography>
                        ) : null}
                        <Button
                          size="small"
                          onClick={() => openEmailView(t)}
                          sx={{ ...actionBtnSx, mt: 0.5 }}
                        >
                          Abrir
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setEmailView(null)} sx={actionBtnSx}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={deleting ? undefined : () => setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {deleteTarget?.clientOffer
            ? "Quitar oferta del presupuesto"
            : deleteTarget?.kind === "rfq"
              ? deleteTarget?.mode === "trash"
                ? "Eliminar petición de coste"
                : "Quitar petición de coste"
              : deleteTarget?.mode === "trash"
                ? "Eliminar en MATRIXBOX"
                : "Quitar de este presupuesto"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget?.clientOffer ? (
              <>
                Se eliminará esta oferta del histórico de este presupuesto. El
                email enviado <strong>sigue</strong> en MATRIXBOX (no se borra
                del buzón):
                <br />
                <em>{deleteTarget?.item?.Subject || "—"}</em>
              </>
            ) : deleteTarget?.kind === "rfq" ? (
              deleteTarget?.mode === "trash" ? (
                <>
                  Se eliminará la petición de coste, sus emails vinculados en el
                  presupuesto <strong>y</strong> se intentará enviar el envío
                  RFQ a la papelera de MATRIXBOX:
                  <br />
                  <em>{deleteTarget?.item?.Subject || "—"}</em>
                </>
              ) : (
                <>
                  Se eliminará la petición de coste y sus vínculos en este
                  presupuesto. Los emails <strong>siguen</strong> en MATRIXBOX:
                  <br />
                  <em>{deleteTarget?.item?.Subject || "—"}</em>
                </>
              )
            ) : deleteTarget?.mode === "trash" ? (
              <>
                Se quitará del presupuesto <strong>y</strong> se enviará a la
                papelera de MATRIXBOX:
                <br />
                <em>{deleteTarget?.item?.Subject || "—"}</em>
                <br />
                <br />
                Si era la petición RFQ, también se eliminará de “Peticiones de
                coste”.
              </>
            ) : (
              <>
                Solo se desvincula del presupuesto. El email{" "}
                <strong>sigue</strong> en MATRIXBOX:
                <br />
                <em>{deleteTarget?.item?.Subject || "—"}</em>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            color={deleteTarget?.mode === "trash" ? "error" : "primary"}
            variant="contained"
            onClick={confirmDeleteComm}
            disabled={deleting}
          >
            {deleting
              ? "Procesando…"
              : deleteTarget?.clientOffer
                ? "Quitar oferta"
                : deleteTarget?.kind === "rfq"
                  ? deleteTarget?.mode === "trash"
                    ? "Eliminar petición"
                    : "Quitar petición"
                  : deleteTarget?.mode === "trash"
                    ? "Eliminar en MATRIXBOX"
                    : "Quitar del presupuesto"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EstimateComunicacionesPanel;
